import { type ButtonHTMLAttributes, type FC, type ReactNode } from "react";

import { clsx } from "clsx";

import { faArrowUp } from "@fortawesome/pro-duotone-svg-icons/faArrowUp";
import { faCheck } from "@fortawesome/pro-duotone-svg-icons/faCheck";
import { faExclamation } from "@fortawesome/pro-duotone-svg-icons/faExclamation";
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons/faSpinnerThird";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Records Status Bar — the single bar at the top of the Process Records stage.
 *
 * Slot-based: header is always present with icon → content → badge/badgeStack
 * → actions. Optional `body`, `chips`, and `context` render below.
 *
 * Variants match `RecordReviewDisplayState` on the server. See
 * `VARIANT_BY_STATE` in `process_records_card.tsx` for the mapping.
 */

// Per D9: the `followup` bar color is retired. All complete states
// (comprehensive, supplemental, follow-up) render `green` — the chip set and
// badge pill carry the classification instead. Color model is now strictly
// gray → indigo → indigo-active → green → red, no backtracking.
export type RsbVariant =
  "none" | "gray" | "indigo" | "active" | "green" | "error";

// Eval-type pills (`comprehensive`, `supplemental`, `followup`) now render via
// the shared rounded-full `EvalTypeBadge`. `RsbBadge` remains as the additive
// section-label primitive — `additional` is the "+ Additional Records" badge
// shown on the post-Add-Records stack — and keeps the eval-type variants only
// as a defensive fallback for any direct caller.
export type RsbBadgeVariant =
  "comprehensive" | "supplemental" | "additional" | "followup";

// `none` renders no chrome: before a record exists there is no pipeline to
// report on, so the panel matches the Reports and Dictation lanes.
const CONTAINER: Record<RsbVariant, string> = {
  none: "",
  gray: "border-slate-200 border-l-slate-400 bg-slate-50",
  indigo: "border-slate-200 border-l-indigo-500 bg-slate-50",
  active: "border-indigo-200 border-l-indigo-500 bg-indigo-50",
  green: "border-emerald-200 border-l-emerald-500 bg-emerald-50",
  error: "border-red-300 border-l-red-600 bg-red-50",
};

const ICON_CLASS: Record<Exclude<RsbVariant, "none">, string> = {
  gray: "bg-slate-200 text-slate-600",
  indigo: "bg-indigo-100 text-indigo-700",
  active: "bg-indigo-600 text-white",
  green: "bg-emerald-500 text-white",
  error: "bg-red-600 text-white",
};

const BADGE_CLASS: Record<RsbBadgeVariant, string> = {
  comprehensive: "bg-indigo-100 text-indigo-700",
  supplemental: "bg-amber-100 text-amber-800",
  additional: "bg-indigo-100 text-indigo-700",
  followup: "bg-emerald-100 text-emerald-700",
};

const BADGE_LABEL: Record<RsbBadgeVariant, string> = {
  comprehensive: "Comprehensive",
  supplemental: "Supplemental",
  additional: "+ Additional Records",
  followup: "Follow-Up",
};

export const RecordsStatusBar: FC<{
  variant: RsbVariant;
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  context?: ReactNode;
  badge?: RsbBadgeVariant;
  badgeStack?: ReactNode;
  actions?: ReactNode;
  body?: ReactNode;
  chips?: ReactNode;
  // Bottom-edge determinate progress bar (0–100) per `.rsb-progress`.
  // Undefined hides the bar.
  progress?: number;
  dimmed?: boolean;
  id?: string;
}> = ({
  variant,
  icon,
  title,
  subtitle,
  context,
  badge,
  badgeStack,
  actions,
  body,
  chips,
  progress,
  dimmed,
  id,
}) => (
  <div
    id={id}
    className={clsx(
      "mb-1 overflow-hidden rounded-[10px]",
      variant !== "none" && "border border-l-4",
      CONTAINER[variant],
      dimmed && "opacity-60",
    )}
  >
    <div
      className={clsx(
        "flex flex-wrap items-center gap-3",
        variant !== "none" ? "px-[18px] py-3.5" : "pb-4",
      )}
    >
      {variant !== "none" && (
        <div
          className={clsx(
            "flex size-6 shrink-0 items-center justify-center rounded-full text-[13px] font-bold",
            ICON_CLASS[variant],
          )}
        >
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div
          className={clsx(
            "text-sm font-semibold",
            variant === "error" ? "text-red-600" : "text-slate-900",
          )}
        >
          {title}
        </div>
        {subtitle && (
          <div className="mt-0.5 text-xs text-slate-500">{subtitle}</div>
        )}
        {context && (
          <div className="mt-[3px] text-[11px] text-slate-500">{context}</div>
        )}
      </div>
      {badgeStack ? (
        <div className="flex shrink-0 items-center gap-1">{badgeStack}</div>
      ) : (
        badge && <RsbBadge variant={badge} />
      )}
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
    {chips && (
      <div className="mt-2.5 mb-1.5 ml-9 flex flex-wrap gap-2">{chips}</div>
    )}
    {body && (
      <div className={clsx(variant !== "none" && "px-[18px] pb-[18px]")}>
        {body}
      </div>
    )}
    {progress !== undefined && (
      <div className="h-[3px] bg-indigo-200">
        <div
          className="h-[3px] rounded-r-[2px] bg-indigo-500 transition-all duration-300"
          style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
        />
      </div>
    )}
  </div>
);

// ----- Badge pill with optional counter --------------------------------------

export const RsbBadge: FC<{
  variant: RsbBadgeVariant;
  counter?: number;
  tone?: "default" | "error";
}> = ({ variant, counter, tone = "default" }) => (
  <span
    className={clsx(
      "shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold tracking-wide uppercase",
      tone === "error" ? "bg-red-100 text-red-700" : BADGE_CLASS[variant],
    )}
  >
    {BADGE_LABEL[variant]}
    {counter !== undefined && counter > 0 && (
      <span className="ml-1 text-indigo-700">+{counter}</span>
    )}
  </span>
);

// ----- Convenience icon components for common RSB states ---------------------

export const RsbUploadIcon: FC = () => <FontAwesomeIcon icon={faArrowUp} />;

export const RsbSpinnerIcon: FC = () => (
  <FontAwesomeIcon icon={faSpinnerThird} spin />
);

export const RsbCheckIcon: FC = () => <FontAwesomeIcon icon={faCheck} />;

export const RsbErrorIcon: FC = () => <FontAwesomeIcon icon={faExclamation} />;

// ----- Ghost action button (matches mockup .rsb-action-btn) ------------------

export const RsbActionButton: FC<
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className">
> = ({ children, ...props }) => (
  <button
    type="button"
    {...props}
    className={clsx(
      "cursor-pointer rounded-md border border-indigo-200 bg-white px-3 py-[5px] text-xs font-medium whitespace-nowrap text-indigo-500 transition",
      "hover:bg-indigo-50",
      "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white",
    )}
  >
    {children}
  </button>
);

// ----- Header-level change link ----------------------------------------------

export const RsbChangeLink: FC<{
  onClick?: () => void;
  locked?: boolean;
}> = ({ onClick, locked }) => {
  if (locked) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer text-xs text-indigo-500 hover:underline"
    >
      &larr; Change
    </button>
  );
};
